import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface MiniChatProps {
  messages: { from: 'client' | 'agent'; text: string }[];
}

function MiniChat({ messages }: MiniChatProps) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100" style={{ maxWidth: 320 }}>
      <div className="bg-[#075E54] px-4 py-3 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center">
          <i className="ri-robot-line text-white text-sm" />
        </div>
        <div>
          <p className="text-white font-semibold text-sm">Automat IA</p>
          <p className="text-green-200 text-xs">online agora</p>
        </div>
        <div className="ml-auto w-2 h-2 rounded-full bg-green-400" />
      </div>
      <div className="bg-[#ECE5DD] px-3 py-3 space-y-2">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.from === 'client' ? 'justify-start' : 'justify-end'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 shadow-sm text-[12px] leading-snug text-gray-800 ${
                msg.from === 'client' ? 'bg-white rounded-tl-sm' : 'bg-[#DCF8C6] rounded-tr-sm'
              }`}
            >
              {msg.from === 'agent' && (
                <p className="text-[10px] font-bold text-[#075E54] mb-0.5">Automat IA</p>
              )}
              {msg.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const blocks = [
  {
    number: '01',
    resolves: 'Resolve: Leads esfriando na fila',
    feature: 'Resposta imediata, 24 horas por dia',
    benefit:
      'Seu lead perguntou preço às 23h? O agente responde na hora com especificação, prazo e condições. Quando seu concorrente acordar, você já agendou a reunião.',
    icon: 'ri-flashlight-fill',
    iconBg: 'bg-coral',
    highlights: ['Responde em menos de 2 segundos', 'Funciona durante a madrugada e fins de semana', 'Nenhum lead fica sem resposta'],
    visual: (
      <MiniChat
        messages={[
          { from: 'client', text: 'Quanto custa o painel solar 550W? (23h17)' },
          { from: 'agent', text: 'Olá! O painel 550W está R$680/unidade. Para projetos com 10+ unidades, 8% de desconto. Posso montar um orçamento completo agora mesmo — qual o consumo médio mensal da instalação?' },
        ]}
      />
    ),
    reverse: false,
  },
  {
    number: '02',
    resolves: 'Resolve: Time afogado em mensagens',
    feature: 'Agente de IA treinado no seu catálogo',
    benefit:
      'Não é chatbot de botão. A IA entende perguntas complexas sobre seus produtos, responde com precisão técnica e libera seu time pra focar em fechar negócio.',
    icon: 'ri-brain-line',
    iconBg: 'bg-navy',
    highlights: ['Treinado com seu catálogo, preços e especificações', 'Responde perguntas técnicas com precisão', 'Libera o time para negociações de alto valor'],
    visual: (
      <MiniChat
        messages={[
          { from: 'client', text: 'O inversor string de 8kW aguenta pico de demanda acima de 15kW por curtos períodos?' },
          { from: 'agent', text: 'Boa pergunta! O inversor 8kW suporta pico de até 120% da potência nominal (9,6kW) por até 10min. Para picos acima disso, recomendo o modelo 12kW com limitador integrado. Envio a comparação técnica?' },
        ]}
      />
    ),
    reverse: true,
  },
  {
    number: '03',
    resolves: 'Resolve: Fora do horário = vendas perdidas',
    feature: 'Operação 24/7 sem custo adicional',
    benefit:
      '70% das mensagens chegam fora do horário. Com a Automat, 100% delas são respondidas — qualificadas e organizadas no CRM pra quando seu time começar o dia.',
    icon: 'ri-moon-fill',
    iconBg: 'bg-[#112448]',
    highlights: ['Todos os leads qualificados e organizados no CRM', 'Reuniões agendadas automaticamente', 'Relatório diário com oportunidades abertas'],
    visual: (
      <MiniChat
        messages={[
          { from: 'client', text: 'Boa tarde! Domingo, 16h30. Vocês fazem projeto de automação para linha de montagem?' },
          { from: 'agent', text: 'Sim! Fazemos projetos completos de automação industrial. Antes de eu montar uma proposta preliminar, me conta: qual o processo que você quer automatizar e qual é o volume de peças/hora esperado?' },
        ]}
      />
    ),
    reverse: false,
  },
];

export default function Solution() {
  const { ref, visible } = useScrollAnimation(0.07);

  return (
    <section id="solution" className="bg-b2bgray py-24 lg:py-32 px-6">
      <div className="max-w-6xl mx-auto" ref={ref}>
        {/* Header */}
        <div
          className={`text-center mb-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
            A Solução
          </span>
        </div>

        <div
          className={`text-center mb-5 transition-all duration-700 delay-75 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <h2 className="font-b2b text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-navy leading-tight max-w-3xl mx-auto">
            Conheça seu novo vendedor.{' '}
            <span className="text-coral">Ele nunca dorme</span>, nunca esquece
            e nunca perde a paciência.
          </h2>
        </div>

        <div
          className={`text-center mb-20 transition-all duration-700 delay-150 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-b2bbody text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            A Automat treina um agente de IA no seu catálogo de produtos e o coloca pra trabalhar
            no seu WhatsApp — atendendo, qualificando e agendando reuniões como se fosse o seu
            melhor vendedor.
          </p>
        </div>

        {/* Blocks */}
        <div className="space-y-20 lg:space-y-28">
          {blocks.map((block, idx) => (
            <SolutionBlock key={block.number} block={block} index={idx} parentVisible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface BlockData {
  number: string;
  resolves: string;
  feature: string;
  benefit: string;
  icon: string;
  iconBg: string;
  highlights: string[];
  visual: React.ReactNode;
  reverse: boolean;
}

function SolutionBlock({
  block,
  index,
  parentVisible,
}: {
  block: BlockData;
  index: number;
  parentVisible: boolean;
}) {
  const { ref, visible } = useScrollAnimation(0.1);
  const isVisible = parentVisible || visible;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-10 lg:gap-16 transition-all duration-800 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: isVisible ? `${index * 80}ms` : '0ms', transitionDuration: '800ms' }}
    >
      {/* Text side */}
      <div className="flex-1 min-w-0">
        {/* Number + resolves */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-9 h-9 rounded-xl ${block.iconBg} flex items-center justify-center flex-shrink-0`}>
            <i className={`${block.icon} text-white text-base`} />
          </div>
          <span className="font-b2bbody text-xs text-coral font-semibold tracking-wider uppercase">
            {block.resolves}
          </span>
        </div>

        <h3 className="font-b2b text-2xl sm:text-3xl font-bold text-navy mb-4 leading-snug">
          {block.feature}
        </h3>
        <p className="font-b2bbody text-gray-600 text-base leading-relaxed mb-7">
          {block.benefit}
        </p>

        {/* Highlights */}
        <ul className="space-y-2.5">
          {block.highlights.map((h) => (
            <li key={h} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-600 text-xs" />
              </div>
              <span className="font-b2bbody text-gray-700 text-sm leading-relaxed">{h}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Visual side */}
      <div className="flex-shrink-0 w-full lg:w-auto flex justify-center">
        <div className="relative">
          {/* Background glow */}
          <div
            className="absolute inset-0 rounded-2xl blur-xl opacity-15 scale-90"
            style={{ background: 'linear-gradient(135deg, #075E54, #E94560)' }}
          />
          <div className="relative">{block.visual}</div>
        </div>
      </div>
    </div>
  );
}